import React from "react";
import {RadioButton, RadioButtonGroup} from "@octopusdeploy/design-system-components";
import SaveDialogLayout from "components/DialogLayout/SaveDialogLayout";
import {DataBaseComponent, DataBaseComponentState} from "components/DataBaseComponent";
import {FeatureToggle, FeatureFlagScopeLevel, FeatureFlagSupportedGranularity} from "client/resources/featureFlagsResource";
import HostedInstanceMultiSelect from "shared/HostedInstanceMultiSelect";
import {HostedInstanceLookupResource} from "client/resources/hostedInstanceLookupResource";
import ReefMultiSelect from "shared/ReefMultiSelect";
import {ReefResource} from "client/resources/reefResource";
import {Text} from "components/form";
import repository from "client/repository";
import {EditFeatureFlagScopeRequest} from "client/resources/editFeatureFlagScopeRequest";
import { Callout } from "@octopusdeploy/design-system-components";
import ExternalLink from "components/Navigation/ExternalLink";

interface Props {
    featureFlag: FeatureToggle;
    instances: HostedInstanceLookupResource[];
    reefs: ReefResource[];
    onSaveClick(): Promise<boolean>;
}

interface State extends DataBaseComponentState {
    scope: FeatureFlagScopeLevel;
    reefIds: string[];
    instanceIds: string[];
    minimumVersion?: string;
    excludedInstanceIds: string[];
}

class EditFeatureFlagScopeDialog extends DataBaseComponent<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            scope: props.featureFlag.AppliesTo.Scope,
            reefIds: props.featureFlag.AppliesTo.ReefIds,
            instanceIds: props.featureFlag.AppliesTo.InstanceIds,
            minimumVersion: props.featureFlag.AppliesTo.MinimumVersion,
            excludedInstanceIds: this.props.featureFlag.AppliesTo.ExcludedInstanceIds
        };
    }

    render() {
        const supportsInstanceGranularity = this.props.featureFlag.Granularity === FeatureFlagSupportedGranularity.Instance;
        const supportsReefGranularity = supportsInstanceGranularity || this.props.featureFlag.Granularity === FeatureFlagSupportedGranularity.Reef;

        return <SaveDialogLayout title={`Edit scope: ${this.props.featureFlag.Name}`}
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 onSaveClick={this.editScope}>

            <Callout type="warning" title="Warning">
                <p>  Have you considered Instances that require manual maintenance (e.g. Philips)? 
                    Please follow <ExternalLink href="https://github.com/OctopusDeploy/hosted-docs/blob/main/how-to/rollout-feature-flag.md#manual-maintenance-instances">these instructions</ExternalLink> on how to roll them out safely!</p>
            </Callout>
            
            <RadioButtonGroup value={this.state.scope} onChange={scope => this.updateScope(scope)}>
                <RadioButton value={FeatureFlagScopeLevel.Global} label="Global" />
                <RadioButton value={FeatureFlagScopeLevel.Scoped} label="Scoped" disabled={!supportsReefGranularity} />
            </RadioButtonGroup>
            
            {supportsInstanceGranularity &&
                <Text label={"Minimum version"}
                      value={this.state.minimumVersion}
                      onChange={version => this.updateMinVersion(version)}
                      hintText="Minimum Octopus Server version"/>}

            {supportsReefGranularity && this.state.scope === FeatureFlagScopeLevel.Scoped &&
                <ReefMultiSelect label="Reefs"
                                items={this.props.reefs}
                                value={this.state.reefIds}
                                onChange={reefIds => this.updateReefIds(reefIds)}
                                autoFocus={true}/>}

            {supportsInstanceGranularity && this.state.scope === FeatureFlagScopeLevel.Scoped &&
                <HostedInstanceMultiSelect label="Instances"
                                           items={this.props.instances}
                                           value={this.state.instanceIds}
                                           onChange={instanceIds => this.updateInstanceIds(instanceIds)}
                                           autoFocus={true}/>}

            {supportsInstanceGranularity &&
                <HostedInstanceMultiSelect label="Explicit Instance Exclusion"
                                           items={this.props.instances}
                                           value={this.state.excludedInstanceIds}
                                           onChange={excludedInstanceIds => this.setState({excludedInstanceIds})}
                                           autoFocus={true}/>}

        </SaveDialogLayout>;
    }
    
    private updateScope(update: FeatureFlagScopeLevel) {
        this.setState({scope: update})
    }
    private updateReefIds(update: string[]) {
        this.setState({reefIds: update})
    }
    private updateInstanceIds(update: string[]) {
        this.setState({instanceIds: update})
    }
    private updateMinVersion(update: string) {
        this.setState({minimumVersion: update})
    }

    private editScope = async (): Promise<boolean> => {
        const scope: EditFeatureFlagScopeRequest = {
            ReefIds: this.state.reefIds,
            InstanceIds: this.state.instanceIds,
            Scope: this.state.scope,
            MinimumVersion: this.state.minimumVersion,
            ExcludedInstanceIds: this.state.excludedInstanceIds
        }

        // Reset properties not applicable for the current scope
        switch (this.state.scope) {
            case FeatureFlagScopeLevel.Global:
                scope.InstanceIds = [];
                scope.ReefIds = [];
                break;
            case FeatureFlagScopeLevel.Scoped:
                // No properties need to be reset
                break;
        }

        return this.doBusyTask(async () => {
            await repository.Configuration.editFeatureFlagScope(this.props.featureFlag.Id, scope);
            return await this.props.onSaveClick();
        });
    }
}

export default EditFeatureFlagScopeDialog;